import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from 'lane-shared/contexts';
import AndroidSmartBanner from './AndroidSmartBanner';
import IOSSmartBanner from './IOSSmartBanner';
import styles from './BannerContainer.scss';
import isAndroidDevice from 'helpers/isAndroidDevice';
import isIOSDevice from 'helpers/isIOSDevice';

/**
 * BannerContainer component that handles smart banner rendering with stable layout.
 * Prevents layout shifts and stretching by managing loading states properly.
 */
export default function BannerContainer() {
  const { whitelabel } = useContext(AppContext);
  const [isContextReady, setIsContextReady] = useState(false);
  const [isPageStable, setIsPageStable] = useState(false);

  useEffect(() => {
    // Wait for whitelabel context to be available
    if (whitelabel !== undefined) {
      setIsContextReady(true);
      
      // Add additional stability check after context is ready
      const stabilityTimer = setTimeout(() => {
        setIsPageStable(true);
      }, 500); // Wait 500ms after context is ready for layout to stabilize

      return () => {
        if (typeof clearTimeout !== 'undefined') {
          clearTimeout(stabilityTimer);
        }
      };
    }

    return undefined;
  }, [whitelabel]);

  // Also listen for window load to ensure page is ready
  useEffect(() => {
    let loadStabilityTimer: number | undefined;
    const handleLoad = () => {
      // Double-check that we have context when page loads
      if (whitelabel !== undefined) {
        setIsContextReady(true);
        loadStabilityTimer = window.setTimeout(() => setIsPageStable(true), 200);
      }
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }

    return () => {
      if (typeof clearTimeout !== 'undefined' && loadStabilityTimer !== undefined) {
        clearTimeout(loadStabilityTimer);
      }

      window.removeEventListener('load', handleLoad);
    };
  }, [whitelabel]);

  // Show stable placeholder while loading
  if (!isContextReady || !isPageStable) {
    return <div className={styles.bannerPlaceholder} data-testid="banner-placeholder" />;
  }

  return (
    <div className={isAndroidDevice() || isIOSDevice() ? styles.bannerContainer : undefined} data-testid="banner-container">
      <AndroidSmartBanner />
      <IOSSmartBanner />
    </div>
  );
}
